const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl'];

const selectSize = {
	xs: 12,
	sm: 6,
	md: 4,
	lg: 4,
	xl: 3,
};

const radioSize = {
	xs: 12,
	sm: 12,
	md: 6,
	lg: 6,
	xl: 6,
};

const getTextSpan = (field, bp) => {
	if ((field.field?.rows ?? 0) > 1) return 12;

	const maxLength = field.field?.max ?? 100;

	if (!field.field?.max && ['currency', 'number'].includes(field.field?.validation)) {
		return selectSize[bp];
	}

	if (field.field?.name === 'Contact') {
		return bp === 'xs' ? 12 : 6;
	}

	if (maxLength <= 4) {
		return 3;
	}

	if (maxLength <= 40) {
		return bp === 'xs' ? 12 : bp === 'sm' ? 6 : 4;
	}

	if (maxLength <= 100) {
		return bp === 'xs' ? 12 : 6;
	}

	return ['xs', 'sm', 'md'].includes(bp) ? 6 : 12;
};

const compactContactSize = {
	xs: 12,
	sm: 4,
	md: 4,
	lg: 4,
	xl: 4,
};

const getFieldSpan = (field, bp) => {
	const fieldName = field.fieldName ?? field.field?.name ?? field.name;
	if (['Mobile', 'Email', 'BirthDate'].includes(fieldName)) {
		return compactContactSize[bp] ?? 4;
	}

	if (field.field?.fieldType === 'radio') {
		return radioSize[bp] ?? 12;
	}

	if (['select', 'date'].includes(field.field?.fieldType)) {
		return selectSize[bp] ?? 12;
	}

	if (field.field?.fieldType === 'text') {
		return getTextSpan(field, bp);
	}

	return 12;
};

const expandRowToFill = (row, bp) => {
	if (row.length <= 1) return row;

	const total = row.reduce((sum, field) => sum + (field.grid?.[bp] ?? 0), 0);

	if (total <= 0 || total >= 12) return row;

	const onlySelects = row.every((field) => field.field?.fieldType === 'select');
	if (onlySelects && 12 - total < 9) return row;

	const scale = 12 / total;

	const expanded = row.map((field, index) => {
		const current = field.grid[bp];
		const raw = current * scale;
		const size = Math.floor(raw);

		return {
			...field,
			grid: { ...field.grid, [bp]: size },
			_meta: {
				index,
				remainder: raw - size,
			},
		};
	});

	const used = expanded.reduce((sum, field) => sum + field.grid[bp], 0);
	const remaining = 12 - used;

	if (remaining > 0) {
		expanded
			.slice()
			.sort((a, b) => b._meta.remainder - a._meta.remainder)
			.slice(0, remaining)
			.forEach((field) => {
				field.grid[bp] += 1;
			});
	}

	return expanded.map(({ _meta, ...field }) => field);
};

const splitMixedRadioRows = (rows, bp) => {
	const nextRows = [];

	for (const row of rows) {
		const radioIndexes = row
			.map((field, index) => (field.field?.fieldType === 'radio' ? index : -1))
			.filter((index) => index !== -1);

		// Leave rows alone if:
		// - no radio
		// - all items are radios
		// - radio is already the only item
		if (
			radioIndexes.length === 0 ||
			radioIndexes.length === row.length ||
			(row.length === 1 && radioIndexes.length === 1)
		) {
			nextRows.push(row);
			continue;
		}

		const nonRadios = row.filter((field) => field.field?.fieldType !== 'radio');
		const radios = row.filter((field) => field.field?.fieldType === 'radio');

		// Each radio gets its own full-width row
		for (const radio of radios) {
			nextRows.push([
				{
					...radio,
					grid: { ...radio.grid, [bp]: 12 },
				},
			]);
		}

		// Repack the remaining items from that row
		if (nonRadios.length > 0) {
			let currentRow = [];
			let used = 0;

			for (const field of nonRadios) {
				const span = field.grid?.[bp] ?? getFieldSpan(field, bp);

				if (used + span > 12 && currentRow.length > 0) {
					nextRows.push(currentRow);
					currentRow = [];
					used = 0;
				}

				currentRow.push({
					...field,
					grid: { ...field.grid, [bp]: span },
				});
				used += span;
			}

			if (currentRow.length > 0) {
				nextRows.push(currentRow);
			}
		}
	}

	return nextRows;
};

const packRowsForBreakpoint = (fields, bp) => {
	const rows = [];
	let currentRow = [];
	let used = 0;

	for (const field of fields) {
		const span = getFieldSpan(field, bp);

		if (used + span > 12 && currentRow.length > 0) {
			rows.push(currentRow);
			currentRow = [];
			used = 0;
		}

		currentRow.push({
			...field,
			grid: { [bp]: span },
		});
		used += span;
	}

	if (currentRow.length > 0) {
		rows.push(currentRow);
	}

	return rows;
};

export const getGridLayout = (fields) => {
	const result = Object.fromEntries(fields.map((field) => [field.name, {}]));

	for (const bp of BREAKPOINTS) {
		// const rows = packRowsForBreakpoint(fields, bp);
		const packedRows = packRowsForBreakpoint(fields, bp);
		const rows = splitMixedRadioRows(packedRows, bp);

		for (const rawRow of rows) {
			const row = rawRow.map((field) => ({
				...field,
				grid: { ...field.grid },
			}));

			const lastField = row[row.length - 1];
			const lastMaxLength = lastField?.field?.max;

			if (
				row.length === 1 &&
				((lastMaxLength && lastMaxLength >= 50) || row[0]?.field?.fieldType === 'radio')
			) {
				lastField.grid[bp] = 12;
			}

			const finalRow = expandRowToFill(row, bp);

			for (const field of finalRow) {
				result[field.name][bp] = field.grid[bp];
			}
		}
	}

	return result;
};
